import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cloud, Search, Shield, ShieldCheck, Crosshair, Bird, Workflow, Radar, Activity, Database, Dog, Bug, Link2, Unlink, CheckCircle2, AlertCircle, ChevronDown, ListChecks, Loader2, X, ExternalLink, KeyRound, Lightbulb } from "lucide-react";
import { useT } from "@/i18n";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";

const ICONS = {
  Cloud, Search, Shield, ShieldCheck, Crosshair, Bird, Workflow, Radar, Activity, Database, Dog, Bug,
};

function PlatformCard({ p, onConnect, onDisconnect, onOpenCases, onOAuthLogin, flagState }) {
  const Icon = ICONS[p.icon] || Shield;
  const [open, setOpen] = useState(false);
  const [creds, setCreds] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const isComingSoon = flagState === "coming_soon";
  const isNew = flagState === "new";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onConnect(p.platform, creds);
      setOpen(false);
      setCreds({});
    } catch (err) {
      // toast handled upstream
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`border ${p.connected ? "border-emerald-300" : isComingSoon ? "border-amber-200" : "border-zinc-200"} bg-white p-6 flex flex-col ${isComingSoon ? "opacity-70" : ""}`} data-testid={`integration-card-${p.platform}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: p.color + "1A" }}>
          <Icon className="w-5 h-5" style={{ color: p.color }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold tracking-tight text-base">{p.name}</h3>
            {isComingSoon && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded" data-testid={`flag-soon-${p.platform}`}>Soon</span>
            )}
            {isNew && !isComingSoon && (
              <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-emerald-500 text-white rounded" data-testid={`flag-new-${p.platform}`}>New</span>
            )}
            {p.connected && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded inline-flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Connected
              </span>
            )}
            {!p.enabled && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-800 rounded inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Disabled by admin
              </span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{p.category}</div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {p.connected ? (
          <>
            <button
              onClick={() => onOpenCases(p)}
              data-testid={`integration-cases-${p.platform}`}
              className="btn-accent inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md"
            >
              <ListChecks className="w-3.5 h-3.5" /> Cases
            </button>
            <button
              onClick={() => onDisconnect(p.platform)}
              data-testid={`integration-disconnect-${p.platform}`}
              className="btn-outline inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md text-red-600 border-red-200 hover:border-red-400"
            >
              <Unlink className="w-3.5 h-3.5" /> Disconnect
            </button>
          </>
        ) : isComingSoon ? (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 font-semibold">
            <AlertCircle className="w-3.5 h-3.5" />
            Coming soon
          </div>
        ) : p.oauth_supported ? (
          // OAuth-supported platforms: ONLY show "Login with X" — no token paste
          p.oauth_configured ? (
            <button
              disabled={!p.enabled}
              onClick={() => onOAuthLogin(p.platform)}
              data-testid={`integration-oauth-${p.platform}`}
              className="btn-accent inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50"
            >
              <Link2 className="w-3.5 h-3.5" /> Login with {p.name}
            </button>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              Admin needs to enable {p.name} login first
            </div>
          )
        ) : (
          // Non-OAuth platforms: keep token paste flow
          <button
            disabled={!p.enabled}
            onClick={() => setOpen((s) => !s)}
            data-testid={`integration-connect-${p.platform}`}
            className="btn-primary inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50"
          >
            <Link2 className="w-3.5 h-3.5" /> Connect
          </button>
        )}
        <button
          onClick={() => setShowGuide((s) => !s)}
          data-testid={`integration-guide-${p.platform}`}
          className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1"
        >
          How to get keys <ChevronDown className={`w-3 h-3 transition-transform ${showGuide ? "rotate-180" : ""}`} />
        </button>
      </div>

      {showGuide && (
        <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded p-4 space-y-3" data-testid={`guide-${p.platform}`}>
          {p.direct_link && (
            <a
              href={p.direct_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`guide-direct-link-${p.platform}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {p.direct_link_label || `Open ${p.name}`}
            </a>
          )}

          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 mb-1.5">Step by step</div>
            <ol className="list-decimal list-inside space-y-1.5 text-xs text-zinc-700">
              {p.guide.map((g, i) => (<li key={i} className="leading-relaxed">{g}</li>))}
            </ol>
          </div>

          {p.token_format && (
            <div className="flex items-start gap-2 text-xs bg-white border border-zinc-200 rounded p-2.5">
              <KeyRound className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-zinc-700 uppercase text-[10px] tracking-wider">Token format</div>
                <div className="text-zinc-600 mt-0.5 font-mono break-all">{p.token_format}</div>
              </div>
            </div>
          )}

          {p.tips && p.tips.length > 0 && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 mb-1.5 flex items-center gap-1">
                <Lightbulb className="w-3 h-3" /> Pro tips
              </div>
              <ul className="space-y-1 text-xs text-zinc-700">
                {p.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-amber-600 shrink-0">•</span>
                    <span className="leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {open && !p.connected && !p.oauth_supported && (
        <form onSubmit={submit} className="mt-4 space-y-3 pt-4 border-t border-zinc-200" data-testid={`connect-form-${p.platform}`}>
          {p.fields.map((f) => (
            <div key={f.key}>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-700">{f.label}</label>
              <input
                type={f.type === "password" ? "password" : "text"}
                placeholder={f.placeholder || ""}
                value={creds[f.key] || ""}
                onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                required
                data-testid={`connect-input-${p.platform}-${f.key}`}
                className="mt-1 w-full px-3 py-2 border border-zinc-200 bg-white text-sm focus:outline-none focus:border-blue-600 rounded-md"
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={submitting}
            data-testid={`connect-submit-${p.platform}`}
            className="btn-accent w-full inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-md disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {submitting ? "Saving..." : "Save & connect"}
          </button>
        </form>
      )}
    </div>
  );
}

function CasesModal({ platform, onClose }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [solving, setSolving] = useState(null);
  const [solution, setSolution] = useState(null);

  useEffect(() => {
    api.get(`/integrations/${platform.platform}/cases`)
      .then((r) => setCases(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load cases"))
      .finally(() => setLoading(false));
  }, [platform.platform]);

  const solve = async (id) => {
    setSolving(id);
    setSolution(null);
    try {
      const { data } = await api.post(`/integrations/${platform.platform}/cases/${id}/solve`);
      setSolution({ id, text: data.solution });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to solve");
    } finally {
      setSolving(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="cases-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-4xl max-h-[85vh] flex flex-col rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{platform.category}</div>
            <h2 className="text-xl font-bold tracking-tight">{platform.name} — Cases</h2>
          </div>
          <button onClick={onClose} data-testid="cases-modal-close" className="p-1 hover:bg-zinc-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-400" /></div>
          ) : cases.length === 0 ? (
            <div className="p-10 text-center text-sm text-zinc-500">No open cases.</div>
          ) : (
            <ul className="divide-y divide-zinc-200" data-testid="cases-list">
              {cases.map((c) => (
                <li key={c.id} className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-zinc-500">{c.id}</div>
                      <div className="font-semibold text-zinc-900 mt-0.5">{c.title}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs">
                        {c.severity && <span className="px-1.5 py-0.5 bg-red-100 text-red-800 rounded font-semibold">{c.severity}</span>}
                        {c.status && <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded">{c.status}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => solve(c.id)}
                      disabled={solving === c.id}
                      data-testid={`case-solve-${c.id}`}
                      className="btn-accent inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-60"
                    >
                      {solving === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                      Solve with AI
                    </button>
                  </div>
                  {solution?.id === c.id && (
                    <div className="mt-3 bg-zinc-50 border border-zinc-200 rounded p-3 text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed" data-testid={`case-solution-${c.id}`}>
                      {solution.text}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Integrations() {
  const { t } = useT();
  const { isHidden, isComingSoon, isNew } = useFeatureFlags();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [casesFor, setCasesFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const connect = async (platform, credentials) => {
    try {
      const { data } = await api.post(`/integrations/${platform}/connect`, { credentials });
      toast.success(data.test_status === "verified" ? "Connected (verified)" : "Saved (verification pending — implementation upcoming)");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connect failed");
      throw err;
    }
  };

  const disconnect = async (platform) => {
    try {
      await api.delete(`/integrations/${platform}`);
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error("Failed to disconnect");
    }
  };

  const oauthLogin = async (platform) => {
    try {
      const { data } = await api.get(`/integrations/${platform}/oauth/start`);
      window.location.href = data.authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OAuth start failed");
    }
  };

  // Handle OAuth callback toast
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauth = params.get("oauth");
    const status = params.get("status");
    const detail = params.get("detail");
    if (oauth && status) {
      if (status === "ok") {
        toast.success(`Connected to ${oauth} via OAuth!`);
      } else {
        toast.error(`OAuth (${oauth}): ${detail || status}`);
      }
      // Clean URL
      window.history.replaceState({}, "", "/app/integrations");
      load();
    }
  }, []);

  // Filter integrations by admin flags. Each platform has a flag of form
  // `integration_<platform>`. State `hidden` → drop from list entirely.
  // `coming_soon` / `new` are forwarded to the card for badge/disabled UX.
  const visibleItems = items.filter((it) => !isHidden(`integration_${it.platform}`));
  const grouped = visibleItems.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <div data-testid="integrations-page">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("integrations_eyebrow")}</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("integrations_h1")}</h1>
      <p className="mt-2 text-zinc-600 max-w-2xl">{t("integrations_sub")}</p>

      {loading ? (
        <div className="mt-10 p-10 flex items-center justify-center border border-zinc-200 bg-white">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <div key={cat} className="mt-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">{cat}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((p) => {
                const fk = `integration_${p.platform}`;
                const flagState = isComingSoon(fk) ? "coming_soon" : isNew(fk) ? "new" : "enabled";
                return (
                  <PlatformCard
                    key={p.platform}
                    p={p}
                    flagState={flagState}
                    onConnect={connect}
                    onDisconnect={disconnect}
                    onOpenCases={(plat) => setCasesFor(plat)}
                    onOAuthLogin={oauthLogin}
                  />
                );
              })}
            </div>
          </div>
        ))
      )}

      {casesFor && <CasesModal platform={casesFor} onClose={() => setCasesFor(null)} />}
    </div>
  );
}
